import { DevTools } from '@components/DevTools';
import { Typography } from '@mui/material';
import { KitsFeedback } from '@pages/ManageProductPage/KitsPage/kitsFeedback';
import { PartsCategories } from '@partful/pages/manage-product/parts-categories/PartsCategories';
import { AvailableFlag, useFeatureFlag } from '@partful/shared/lib/hooks/useFeatureFlag';
import { ProductContextProvider } from '@partful/shared/lib/providers/ProductContextProvider';
import { useTenant } from '@providers/Tenant';
import { useTranslation } from 'react-i18next';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import { useDecodedParams } from 'src/hooks/useDecodedParams';
import { NotFoundPageAuthorised } from 'src/pages/NotFoundPageAuthorised';
import { ManageTemplate } from './components/ManageTemplate';
import { Kits } from './KitsPage/kits';
import { ProductOverview } from './ProductOverviewPage/ProductOverview';
import { SalesBOM } from './SalesBOMPage/salesBOM';

export default function ManageProductPage() {
  const { path } = useRouteMatch();
  const { productId } = useDecodedParams<{ productId: string }>();
  const { t } = useTranslation('manageProduct');
  const { enabledFeatures } = useTenant();
  const { isFlagOn } = useFeatureFlag();

  return (
    <Switch>
      <Route exact path={path}>
        <Redirect to="/product-library" />
      </Route>
      {/* Product Overview */}
      <Route exact path={`${path}/:productId`}>
        <ManageTemplate page="product-overview" pageTitle="Product Overview">
          <ProductOverview />
        </ManageTemplate>
      </Route>
      {/* Part Categories */}
      <Route exact path={`${path}/:productId/parts-categories`}>
        <ManageTemplate
          page="parts-categories"
          pageTitle={t('partsCategories.title')}
          pageDescription={
            <>
              <Typography>{t('partsCategories.description.primary')}</Typography>
              <Typography>{t('partsCategories.description.secondary')}</Typography>
            </>
          }
        >
          <ProductContextProvider name={productId}>
            <PartsCategories />
          </ProductContextProvider>
        </ManageTemplate>
      </Route>
      {/* Sales BOM */}
      <Route path={`${path}/:productId/sales-bom`}>
        <ManageTemplate
          page="sales-bom"
          pageTitle={t('salesBOM.title')}
          pageDescription={<Typography>{t('salesBOM.description')}</Typography>}
        >
          <SalesBOM />
        </ManageTemplate>
      </Route>
      {/* Kits */}
      {enabledFeatures.includes('kits') && (
        <Route path={`${path}/:productId/kits`}>
          <ManageTemplate
            page="kits"
            pageTitle={t('kits.title')}
            pageDescription={
              <>
                <Typography>{t('kits.description.primary')}</Typography>
                <Typography>{t('kits.description.secondary')}</Typography>
              </>
            }
          >
            {isFlagOn(AvailableFlag.kitsFeedback) ? /* istanbul ignore next */ <KitsFeedback /> : <Kits />}
          </ManageTemplate>
        </Route>
      )}
      <NotFoundPageAuthorised />
      {process.env.REACT_APP_DEV_TOOLS_ENABLED === 'true' && /* istanbul ignore next */ <DevTools />}
    </Switch>
  );
}
